import React from "react";
import { HOME_THEME as T } from "../../lib/homeTheme";
import { StaffDivider } from "./StaffDivider";

export interface HomeIndexItem {
  label: string;
  meta?: string;
  onClick?: () => void;
  disabled?: boolean;
}

interface HomeIndexSectionProps {
  title: string;
  items: HomeIndexItem[];
  children?: React.ReactNode;
  compact?: boolean;
}

export const HomeIndexSection: React.FC<HomeIndexSectionProps> = ({
  title,
  items,
  children,
  compact = false,
}) => {
  return (
    <section className={compact ? "py-8 md:py-10" : "py-14 md:py-20"}>
      <StaffDivider className={compact ? "mb-8 md:mb-10" : "mb-14 md:mb-16"} />

      <h2
        className={`font-light tracking-tight text-left bg-clip-text text-transparent ${
          compact
            ? "mb-6 md:mb-8 text-[clamp(1.5rem,3vw,2.25rem)]"
            : "mb-10 md:mb-14 text-[clamp(1.75rem,4vw,3rem)]"
        }`}
        style={{ backgroundImage: T.textGradient }}
      >
        {title}
      </h2>

      {items.length > 0 && (
        <ul>
          {items.map((item, i) => {
            const Tag = item.onClick && !item.disabled ? "button" : "div";
            return (
              <li key={`${item.label}-${i}`} style={{ borderBottom: `1px solid ${T.lineItem}` }}>
                <Tag
                  type={item.onClick ? "button" : undefined}
                  onClick={item.onClick}
                  disabled={item.disabled}
                  className={
                    "group flex w-full items-baseline gap-4 md:gap-8 py-5 md:py-6 text-left transition-all duration-300 " +
                    (item.onClick && !item.disabled
                      ? "hover:pl-2 hover:bg-[rgba(196,181,253,0.04)] cursor-pointer"
                      : "")
                  }
                >
                  <span
                    className="w-8 shrink-0 text-xs tabular-nums tracking-wider"
                    style={{ color: T.dim }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span
                    className={
                      "flex-1 font-light tracking-tight text-[clamp(1.125rem,2.5vw,1.75rem)] transition-colors duration-300 " +
                      (item.onClick ? "group-hover:text-[#c4b5fd]" : "")
                    }
                    style={{ color: item.disabled ? T.dim : T.text }}
                  >
                    {item.label}
                  </span>
                  {item.meta && (
                    <span className="shrink-0 text-xs tracking-wide" style={{ color: T.muted }}>
                      {item.meta}
                    </span>
                  )}
                </Tag>
              </li>
            );
          })}
        </ul>
      )}

      {children}
    </section>
  );
};
